import { getCookie, setCookie } from 'src/utils/cookies'

const COOKIE_NAME = 'dkt_session_attribution'
// Rolling 30-minute session window (GA-style), renewed on each qualifying write.
const SESSION_TTL_MIN = 30
const REF_DOMAIN_KEY = 'ref_domain'

// Keys read straight from the URL query string, in canonical order.
// `ref_domain` is NOT here on purpose — it never comes from the URL (it would be
// a spoof vector via `?ref_domain=...`), only from `document.referrer`.
export const URL_ATTRIBUTION_KEYS = [
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
] as const

function pickKeys(
  params: URLSearchParams,
  keys: readonly string[]
): URLSearchParams {
  const out = new URLSearchParams()

  for (const key of keys) {
    const value = params.get(key)

    if (value) out.append(key, value)
  }

  return out
}

// Canonical `key=value&key=value` string from a full attribution record:
// URL keys first (in order), then `ref_domain`. Use on a session record, not on
// raw URL params.
export function buildAttributionString(params: URLSearchParams): string {
  const out = pickKeys(params, URL_ATTRIBUTION_KEYS)

  const refDomain = params.get(REF_DOMAIN_KEY)

  if (refDomain) out.append(REF_DOMAIN_KEY, refDomain)

  return out.toString()
}

function externalRefDomain(referrer: string): string {
  if (!referrer || typeof window === 'undefined') return ''

  try {
    const strip = (host: string) => host.replace(/^www\./, '')
    const refHost = strip(new URL(referrer).hostname)
    const currentHost = strip(window.location.hostname)

    if (!refHost || refHost === currentHost) return ''

    return refHost
  } catch {
    return ''
  }
}

export function getSessionAttribution(): URLSearchParams {
  try {
    return new URLSearchParams(getCookie(COOKIE_NAME) ?? '')
  } catch {
    return new URLSearchParams()
  }
}

// Side effect: (re)writes the cookie, renewing the 30-minute session window.
// - utm_* + gclid: last-touch — any of them present in `search` replaces that
//   whole group; otherwise the stored group is kept.
// - ref_domain: first-touch — the first external referrer of the session wins,
//   is carried across later utm rewrites, and is only recorded when not stored.
// No-op when there is nothing to record (no url params, no new ref_domain).
export function captureSessionAttribution(
  search: string,
  referrer: string
): void {
  if (typeof document === 'undefined') return

  const stored = getSessionAttribution()

  const freshUrlPart = pickKeys(
    new URLSearchParams(search),
    URL_ATTRIBUTION_KEYS
  ).toString()

  const storedRefDomain = stored.get(REF_DOMAIN_KEY) ?? ''
  const newRefDomain = storedRefDomain ? '' : externalRefDomain(referrer)

  // Nothing new to record → no write, no window renewal.
  if (!freshUrlPart && !newRefDomain) return

  const urlPart =
    freshUrlPart || pickKeys(stored, URL_ATTRIBUTION_KEYS).toString()

  const refDomain = storedRefDomain || newRefDomain

  const out = new URLSearchParams(urlPart)

  if (refDomain) out.append(REF_DOMAIN_KEY, refDomain)

  const value = out.toString()

  if (!value) return

  setCookie(COOKIE_NAME, value, { expiresInMinutes: SESSION_TTL_MIN })
}

// last-touch group from `search` (or the session fallback), always merged with
// the first-touch `ref_domain` from the session (it never travels in the URL).
export function resolveSessionAttributionString(search: string): string {
  const stored = getSessionAttribution()

  const fromSearch = pickKeys(
    new URLSearchParams(search),
    URL_ATTRIBUTION_KEYS
  ).toString()

  const urlPart =
    fromSearch || pickKeys(stored, URL_ATTRIBUTION_KEYS).toString()

  const out = new URLSearchParams(urlPart)

  const refDomain = stored.get(REF_DOMAIN_KEY)

  if (refDomain && !out.has(REF_DOMAIN_KEY)) {
    out.append(REF_DOMAIN_KEY, refDomain)
  }

  return out.toString()
}
